import { Button, LinkButton } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { links } from '@/ui/constants/links'
import { ErrorLayout } from '@/ui/layouts/ErrorLayout'
import { ClipboardIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'

export interface ErrorViewProps {
  onReload: () => void
  fullScreen?: boolean
  error?: Error
}

export function ErrorView({ fullScreen, error }: ErrorViewProps) {
  const [isCopying, setIsCopying] = useState(false)

  function handleCopyError() {
    if (!error?.message) return

    setIsCopying(true)
    navigator.clipboard
      .writeText(`${error.name}: ${error.message}\n\nStack: ${error.stack}`)
      .then(() => {
        toast.success('Error details copied to clipboard')
        setIsCopying(false)
      })
      .catch((err) => {
        console.error('Failed to copy error details:', err)
        toast.error('Failed to copy error details')
        setIsCopying(false)
      })
  }

  return (
    <ErrorLayout fullScreen={fullScreen}>
      <Panel.Wrapper className="w-full max-w-xl p-6 text-center">
        <Typography variant="h2" className="mb-2" gradient>
          We&apos;ve encountered an issue
        </Typography>

        {error?.message.includes('An unknown RPC error occurred') && (
          <Typography variant="p" className="mb-6 text-sm opacity-70">
            The Hyperliquid RPC might be temporarily unavailable. Please try again later.
          </Typography>
        )}

        <div>
          <Typography variant="p" className="mb-4">
            Please follow for updates or reach out directly on socials
          </Typography>
          <div className="flex w-full items-center justify-center gap-4">
            <LinkButton
              external
              href={links.social.x}
              variant="ghost"
              size="sm"
              className="w-32 text-secondary-foreground hover:text-foreground"
            >
              X
            </LinkButton>
            <LinkButton
              external
              href={links.social.telegram}
              variant="ghost"
              size="sm"
              className="w-32 text-secondary-foreground hover:text-foreground"
            >
              Telegram
            </LinkButton>
          </div>
        </div>

        {error?.message && (
          <div className="mt-4">
            <Button
              onClick={handleCopyError}
              variant="text"
              disabled={isCopying}
              className="text-muted-foreground text-sm hover:text-foreground"
            >
              <ClipboardIcon className="mr-1 h-4 w-4" /> {isCopying ? 'Copying...' : 'Copy error to clipboard'}
            </Button>
          </div>
        )}
      </Panel.Wrapper>
    </ErrorLayout>
  )
}
